<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

defineEmits(['open']);

const { t } = useI18n();

const isDone = computed(() => props.task.status === 'done');
const labels = computed(() => props.task.labels || []);
const coverColor = computed(() => props.task.cover_color || '');
const hasDescription = computed(() => Boolean(props.task.description));
const attachmentsCount = computed(() => props.task.attachments_count || 0);

const PRIORITY_DOT = {
  low: '#64748b',
  medium: '#f59e0b',
  high: '#ef4444',
};
const priorityDot = computed(
  () => PRIORITY_DOT[props.task.priority] || PRIORITY_DOT.low
);
const PRIORITY_LABEL_KEYS = {
  low: 'TASKS.PRIORITY.LOW',
  medium: 'TASKS.PRIORITY.MEDIUM',
  high: 'TASKS.PRIORITY.HIGH',
};
const priorityLabel = computed(() =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(PRIORITY_LABEL_KEYS[props.task.priority] || PRIORITY_LABEL_KEYS.low)
);

const checklist = computed(() => props.task.checklist || []);
const checklistTotal = computed(() => checklist.value.length);
const checklistDone = computed(
  () => checklist.value.filter(item => item.done).length
);
const checklistLabel = computed(
  () => `${checklistDone.value}/${checklistTotal.value}`
);
const checklistComplete = computed(
  () => checklistTotal.value > 0 && checklistDone.value === checklistTotal.value
);

const dueLabel = computed(() => {
  if (!props.task.due_date) return '';
  const [, month, day] = props.task.due_date.split('-');
  return `${day}/${month}`;
});

const isOverdue = computed(() => {
  if (!props.task.due_date || props.task.status === 'done') return false;
  const today = new Date().toISOString().slice(0, 10);
  return props.task.due_date < today;
});
</script>

<template>
  <div
    class="flex flex-col overflow-hidden border rounded-lg cursor-pointer bg-n-solid-1 border-n-weak hover:border-n-strong hover:shadow-md hover:-translate-y-px transition-all"
    @click="$emit('open', task)"
  >
    <div
      v-if="coverColor"
      class="h-8 w-full"
      :style="{ backgroundColor: coverColor }"
    />

    <div class="flex flex-col gap-1.5 p-2.5">
      <div v-if="labels.length" class="flex flex-wrap gap-1">
        <span
          v-for="(label, index) in labels"
          :key="index"
          class="h-2 rounded-full min-w-[2.5rem]"
          :style="{ backgroundColor: label.color }"
          :title="label.name || ''"
        />
      </div>

      <p
        class="flex items-start gap-1.5 text-sm"
        :class="isDone ? 'text-n-slate-10 line-through' : 'text-n-slate-12'"
      >
        <span
          v-if="isDone"
          class="i-lucide-check-circle-2 size-3.5 shrink-0 mt-0.5 text-n-teal-11"
        />
        <span class="line-clamp-3">{{ task.title }}</span>
      </p>

      <div class="flex flex-wrap items-center gap-2.5 text-n-slate-11">
        <span class="flex items-center gap-1 text-xs" :title="priorityLabel">
          <span
            class="rounded-full size-2"
            :style="{ backgroundColor: priorityDot }"
          />
          {{ priorityLabel }}
        </span>

        <span
          v-if="task.due_date"
          class="flex items-center gap-1 text-xs"
          :class="isOverdue ? 'text-n-ruby-11 font-medium' : ''"
        >
          <span class="i-lucide-clock size-3" />
          {{ dueLabel }}
        </span>

        <span
          v-if="hasDescription"
          class="i-lucide-align-left size-3.5"
          :title="t('TASKS.CARD.DESCRIPTION')"
        />

        <span v-if="attachmentsCount" class="flex items-center gap-1 text-xs">
          <span class="i-lucide-paperclip size-3" />
          {{ attachmentsCount }}
        </span>

        <span
          v-if="checklistTotal"
          class="flex items-center gap-1 px-1 text-xs rounded"
          :class="checklistComplete ? 'text-n-teal-11 bg-n-teal-3' : ''"
        >
          <span class="i-lucide-check-square size-3" />
          {{ checklistLabel }}
        </span>

        <Avatar
          v-if="task.assignee"
          class="ml-auto"
          :name="task.assignee.name"
          :src="task.assignee.avatar_url"
          :size="20"
        />
      </div>
    </div>
  </div>
</template>
